import { RefObject, useMemo, useRef, PointerEvent, KeyboardEvent } from 'react';
import { Chess, Move } from 'chess.js';
import { proxy, useSnapshot } from 'valtio';

import {
  BOARD_SOUND_THEMES,
  BoardSoundThemes,
  ChessBoard,
  type ChessBoardProps,
} from '@exports/ChessBoard';
import { UserArrow } from '@exports/ChessBoard/ChessBoard';
import { toggleUserArrow } from '@exports/ChessBoard/utils/toggleUserArrow';
import {
  AllowedAction,
  initSelection,
  SelectionFSM,
  SelectionIdle,
} from '@exports/ChessBoard/hooks/useChessGame.fsm';
import { AudioManager } from '@libs/audio';
import { applyNullMove } from '@libs/chess/applyNullMove';
import { ChessWinner, Color, PieceCode, PromotionPiece, SquareName } from '@libs/chess/types';
import { delay } from '@libs/delay';

const MOVE_DELAY_MS = 310;
const CAPTURE_DELAY_MS = 210;

type SoundPack = {
  move(mode: 'drop' | 'full'): void;
  capture(mode: 'drop' | 'full'): void;
};

const NOOP_SOUND_PACK: SoundPack = {
  move: () => {},
  capture: () => {},
};

export type MoveInfo = {
  from: SquareName;
  to: SquareName;
  promotion?: PromotionPiece;
};

export type Props = {
  fen: string;
  side: 'white' | 'black' | 'all' | 'none';
  lastMove?: MoveInfo;
  premove?: MoveInfo;
  allowPremove?: boolean;
  // Разрешить ходить вне очереди, т.е. несколько раз подряд
  // (требует side='all', несовместим с allowPremove)
  allowOutOfOrder?: boolean;
  autoPromotion?: PromotionPiece;
  boardSounds?: BoardSoundThemes;
  silent?: boolean;
  blind?: boolean;
  doNotResetArrows?: boolean;
  noValidation?: boolean;
  onPremove?: (m: MoveInfo | null) => void;
  onMove?: (m: MoveInfo, san: string, fen: string) => void;
  // Срабатывает вместо onMove
  onOutOfOrderMove?: (m: MoveInfo, san: string, fen: string) => void;
  onUserArrow?: (a: UserArrow | null, nextUserArrows?: UserArrow[]) => void;
  onRotate?: (next: 'black' | 'white') => void;
  //
  orientation: ChessBoardProps['orientation'];
  //
  className?: ChessBoardProps['className'];
  theme?: ChessBoardProps['theme'];
  notation?: ChessBoardProps['notation'];
  noAnimation?: ChessBoardProps['noAnimation'];
  piecesAnimation?: ChessBoardProps['piecesAnimation'];
  noShadow?: ChessBoardProps['noShadow'];
  noAutoBorderRadius?: ChessBoardProps['noAutoBorderRadius'];
  noBorderRadius?: ChessBoardProps['noBorderRadius'];
  modal?: ChessBoardProps['modal'];
  valuation?: ChessBoardProps['valuation'];
  valuationValue?: ChessBoardProps['valuationValue'];
  arrows?: ChessBoardProps['arrows'];
  winner?: ChessBoardProps['winner'];
};

/*
    ____,-------------------------------,____
    \   |           Component           |   /
    /___|-------------------------------|___\
*/

export function ChessGame(props: Props) {
  const {
    fen,
    side,
    allowPremove,
    blind,
    //
    className,
    theme,
    orientation,
    notation,
    noAnimation,
    piecesAnimation,
    noShadow,
    noAutoBorderRadius,
    noBorderRadius,
    modal,
    valuation,
    valuationValue,
    arrows,
    onRotate,
  } = props;

  if (side === 'all' && allowPremove) {
    throw new Error('ChessGame: allowPremove is not supported with side=all');
  }

  if (props.allowOutOfOrder && side !== 'all') {
    throw new Error('ChessGame: allowOutOfOrder requires side=all');
  }

  if (props.allowOutOfOrder && allowPremove) {
    throw new Error('ChessGame: allowOutOfOrder is not supported with allowPremove');
  }

  // store
  const pRef = useRef(props);
  pRef.current = props;
  const store = useMemo(() => new Store(pRef), []);
  store.update();

  // snapshot
  const snap = useSnapshot(store.state) as IState;

  return (
    <ChessBoard
      className={className}
      theme={theme}
      orientation={orientation}
      position={fen}
      notation={notation}
      noAnimation={noAnimation}
      piecesAnimation={piecesAnimation}
      noShadow={noShadow}
      noAutoBorderRadius={noAutoBorderRadius}
      noBorderRadius={noBorderRadius}
      noPieces={blind}
      noWaypoints={blind}
      modal={modal}
      valuation={valuation}
      valuationValue={valuationValue}
      arrows={arrows}
      onRotate={onRotate}
      //
      {...snap}
      {...store.handlers}
    />
  );
}

/*
    ____,-------------------------------,____
    \   |             Store            |   /
    /___|-------------------------------|___\
*/

type IHandlers = Pick<
  ChessBoardProps,
  | 'onPieceDrag'
  | 'onSquareClick'
  | 'onSquarePointerDown'
  | 'onSquareDrop'
  | 'onPromotion'
  | 'onKeyDown'
  | 'onArrowDrag'
  | 'onArrowPlace'
  | 'onDropOutside'
  | 'onDragAbort'
>;

type IState = Pick<
  ChessBoardProps,
  | 'interactive'
  | 'selectedFrom'
  | 'selectedTo'
  | 'isPremove'
  | 'premoveFrom'
  | 'premoveTo'
  | 'promotingFrom'
  | 'promotingTo'
  | 'winner'
  | 'moves'
>;

class State implements IState {
  interactive = 'none' as const;
}

class Store {
  public state: IState;

  private chess: Chess;
  private fsm: SelectionFSM;
  private prevMoveFSM: SelectionFSM;
  private prevProps: Props;
  private soundPack: SoundPack = NOOP_SOUND_PACK;
  private readonly noValidation: boolean;

  constructor(private propsRef: RefObject<Props>) {
    const { fen, lastMove, boardSounds, noValidation } = propsRef.current;

    this.noValidation = noValidation ?? false;

    this.chess = new Chess(fen, {
      enable960: true,
      skipValidation: this.noValidation,
    });

    this.fsm = initSelection(
      lastMove && {
        from: lastMove.from,
        to: lastMove.to,
        action: 'move',
      },
    );

    this.prevMoveFSM = this.fsm;
    this.prevProps = this.props;
    this.state = proxy(new State());

    this.syncState();
    this.loadSounds(boardSounds);
  }

  get props(): Props {
    return this.propsRef.current;
  }

  get winner(): ChessWinner | undefined {
    return this.chess.isGameOver() ? mapWinner(this.chess) : (this.props.winner ?? undefined);
  }

  update() {
    const next = this.props;
    const prev = this.prevProps;

    const fenChanged = next.fen !== this.chess.fen();
    const moveChanged = !isEqualMove(next.lastMove, prev.lastMove);
    const soundsChanged = next.boardSounds !== prev.boardSounds;

    if (this.props === this.prevProps && !fenChanged) {
      return;
    }

    if (fenChanged) {
      if (next.lastMove && moveChanged) {
        this.applyExternalMove(next.lastMove, next.fen, next.silent);
      } else {
        this.chess.load(next.fen, { skipValidation: this.noValidation });
      }
    }

    if (
      fenChanged &&
      // pending premove
      this.fsm.name === 'targeting' &&
      this.fsm.action === 'premove'
    ) {
      this.prevMoveFSM = initSelection(
        next.lastMove && {
          from: next.lastMove.from,
          to: next.lastMove.to,
          action: 'move',
        },
      );
      this.fsm = new SelectionIdle().transition({
        allowedAction: this.getAllow(),
        from: this.fsm.from,
        chess: this.chess,
        allowOutOfOrder: this.props.allowOutOfOrder,
      });
    } else {
      const selectionChanged = moveChanged ? !isEqualSelection(next.lastMove, this.fsm) : false;

      if (fenChanged || selectionChanged) {
        this.fsm = initSelection(
          next.lastMove && {
            from: next.lastMove.from,
            to: next.lastMove.to,
            action: 'move',
          },
        );
        this.prevMoveFSM = this.fsm;
      }
    }

    if (soundsChanged) {
      this.loadSounds(next.boardSounds);
    }

    this.syncState();

    this.prevProps = this.props;
  }

  private applyExternalMove(lastMove: MoveInfo, expectedFen: string, silent?: boolean) {
    try {
      const m = this.chess.move({
        from: lastMove.from,
        to: lastMove.to,
        promotion: lastMove.promotion,
      });

      if (!silent) {
        if (m.captured) {
          this.soundPack.capture('full');
        } else {
          this.soundPack.move('full');
        }
      }

      if (this.chess.fen() !== expectedFen) {
        this.chess.load(expectedFen, { skipValidation: this.noValidation });
      }
    } catch {
      this.chess.load(expectedFen, { skipValidation: this.noValidation });
    }
  }

  private setFsm(next: SelectionFSM) {
    if (this.fsm === next) return;

    if (next.name === 'selected' && next.action === 'move') {
      this.prevMoveFSM = next;
    }

    this.fsm = next;
    this.syncState();
  }

  private getAllow(): AllowedAction {
    return mapAllowedAction(
      this.props.side,
      this.chess.turn(),
      this.props.allowPremove ?? false,
      Boolean(this.chess.isGameOver() || this.props.winner),
    );
  }

  private handleMove(move: Move, mode: 'drop' | 'full' = 'full', isOutOfOrder = false) {
    const m =
      this.props.autoPromotion && move.promotion
        ? {
            from: move.from,
            to: move.to,
            promotion: move.promotion as PromotionPiece,
          }
        : {
            from: move.from,
            to: move.to,
            piece: mapMoveToPieceCode(move),
          };

    if (this.fsm.action === 'premove') {
      this.props.onPremove?.(m);
      this.soundPack.move('drop'); // sic! всегда drop, нет анимаций
    } else {
      if (isOutOfOrder) applyNullMove(this.chess);

      const cb = isOutOfOrder ? this.props.onOutOfOrderMove : this.props.onMove;
      const cm = this.chess.move(move);
      cb?.(m, cm.san, this.chess.fen());

      if (cm.captured) {
        this.soundPack.capture(mode);
      } else {
        this.soundPack.move(mode);
      }
    }
  }

  private loadSounds(boardSounds?: BoardSoundThemes) {
    const resources = boardSounds && BOARD_SOUND_THEMES[boardSounds];

    if (resources) {
      AudioManager.instance.load(resources).then((p) => {
        this.soundPack = {
          move: (mode) => {
            if (mode === 'drop') p.move.play();
            else delay(MOVE_DELAY_MS).then(() => p.move.play());
          },
          capture: (mode) => {
            if (mode === 'drop') p.capture.play();
            else delay(CAPTURE_DELAY_MS).then(() => p.capture.play());
          },
        };
      });
    } else {
      this.soundPack = NOOP_SOUND_PACK;
    }
  }

  private syncState() {
    const { state, fsm, prevMoveFSM: lastMove } = this;
    const premove = this.props.premove;

    const allow = this.getAllow();

    const gameOver = !!this.winner;
    const showLastMove = gameOver || allow === 'premove' || fsm.name === 'idle';

    state.interactive = mapInteractive(allow, this.chess.turn(), this.props.allowOutOfOrder);
    state.isPremove = !gameOver && (fsm.action === 'premove' || !!premove);
    state.selectedFrom = showLastMove ? lastMove.from : fsm.from;
    state.selectedTo = showLastMove ? lastMove.to : fsm.to;
    state.premoveFrom = gameOver ? undefined : fsm.action === 'premove' ? fsm.from : premove?.from;
    state.premoveTo = gameOver ? undefined : fsm.action === 'premove' ? fsm.to : premove?.to;
    state.moves = this.winner ? undefined : fsm.moves?.map((m) => m.to);
    state.promotingFrom = fsm.promotion?.from;
    state.promotingTo = fsm.promotion?.to;
    state.winner = this.winner;
  }

  handlers: IHandlers = {
    // ---- Selection ----

    //
    // Выбор клетки происходит по ponterdown, а вот снятие двухфазное:
    //  - PointerDown: устанавливает флаг (в SelectionTargeting.transition)
    //  - Click: проверяет флаг и отменяет выбор клетки (в SelectionTargeting.transitionCleanup)
    //
    // Без этого при перетаскивании ранее выбранной фигуры выбор сначала снимется,
    // и вернётся при перемещении мышки
    //

    onSquarePointerDown: (s: SquareName, _: unknown, e: PointerEvent) => {
      // игнорировать ЛКМ при зажатом ctrl/alt (режим рисования стрелочек)
      if (e.ctrlKey || e.altKey || e.shiftKey) return;

      const allow = this.getAllow();
      if (allow === 'none' || e.button !== 0) return;

      let next: SelectionFSM =
        this.fsm.name === 'promoting' || this.fsm.name === 'selected'
          ? this.fsm.toIdle()
          : this.fsm;

      // Сбросим premove
      if (this.fsm.name === 'selected' && this.fsm.action === 'premove') {
        this.props.onPremove?.(null);
      }

      // CORNER CASE: клик на ту же пешку, которую двигали, но уже в новой клетке
      if (this.fsm.name === 'promoting' && this.fsm.to === s) {
        this.setFsm(next);
        return;
      }

      switch (next.name) {
        case 'idle':
          next = next.transition({
            from: s,
            allowedAction: allow,
            chess: this.chess,
            allowOutOfOrder: this.props.allowOutOfOrder,
          });
          break;
        case 'targeting':
          next = next.transition({
            to: s,
            chess: this.chess,
            canReset: true,
            canReselect: true,
            moveCallback: (m, isOutOfOrder) => this.handleMove(m, 'full', isOutOfOrder),
            autoPromotion: this.props.autoPromotion,
          });
          break;
      }

      if (next.name === 'promoting') {
        this.soundPack.move('full');
      }

      // сбросить стрелочки при ЛКМ
      if (
        e.button === 0 &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey &&
        !this.props.doNotResetArrows &&
        next.name === 'idle'
      ) {
        const hasArrowsToReset = this.props.arrows?.some((a) => a.type === 'user');
        if (hasArrowsToReset) {
          this.props.onUserArrow?.(null);
        }
      }

      this.setFsm(next);
    },

    onSquareClick: () => {
      if (this.fsm.name === 'targeting') {
        this.setFsm(this.fsm.transitionCleanup());
      }
    },

    // ---- Drag ----

    onPieceDrag: (s: SquareName) => {
      if (this.fsm.name === 'idle') {
        this.setFsm(
          this.fsm.transition({
            allowedAction: this.getAllow(),
            from: s,
            chess: this.chess,
            allowOutOfOrder: this.props.allowOutOfOrder,
          }),
        );
        return true;
      }

      return this.fsm.name === 'targeting' && this.fsm.from === s;
    },

    onSquareDrop: (_from: SquareName, to: SquareName) => {
      if (this.fsm.name !== 'targeting' || this.winner) return;

      const next = this.fsm.transition({
        to,
        chess: this.chess,
        canReset: true,
        canReselect: false,
        moveCallback: (m, isOutOfOrder) => this.handleMove(m, 'drop', isOutOfOrder),
        autoPromotion: this.props.autoPromotion,
      });

      if (next.name === 'promoting') {
        this.soundPack.move('drop');
      }

      this.setFsm(next);
    },

    onDropOutside: () => {
      this.setFsm(new SelectionIdle());
    },

    onDragAbort: () => {
      this.setFsm(new SelectionIdle());
    },

    // --- Promotion ---

    onPromotion: (promoteTo: PromotionPiece) => {
      if (this.fsm.name !== 'promoting') return;

      const move: MoveInfo = {
        from: this.fsm.promotion.from,
        to: this.fsm.promotion.to,
        promotion: promoteTo,
      };

      if (this.fsm.action === 'premove') {
        this.setFsm(this.fsm.toSelected());
        this.props.onPremove?.(move);
        return;
      }

      const isOutOfOrder = this.fsm.isOutOfOrder;
      if (isOutOfOrder) {
        applyNullMove(this.chess);
      }
      const san = this.chess.move({
        ...this.fsm.promotion,
        promotion: promoteTo,
      }).san;
      this.setFsm(this.fsm.toSelected());
      if (isOutOfOrder) {
        this.props.onOutOfOrderMove?.(move, san, this.chess.fen());
      } else {
        this.props.onMove?.(move, san, this.chess.fen());
      }
    },

    // --- Keyboard ---

    onKeyDown: (e: KeyboardEvent) => {
      if (
        e.code === 'Escape' &&
        this.fsm.name !== 'idle' &&
        (this.fsm.name !== 'selected' || this.fsm.action === 'premove')
      ) {
        if (this.fsm.action === 'premove') {
          this.props.onPremove?.(null);
        }
        this.setFsm(this.fsm.toIdle());
      }
    },

    // --- Arrows ---

    onArrowPlace: (arrow: UserArrow) => {
      this.props.onUserArrow?.(arrow, toggleUserArrow(this.props.arrows, arrow));
    },

    onArrowDrag: () => {
      if (this.fsm.name !== 'idle' && this.fsm.name !== 'selected') {
        this.setFsm(this.fsm.toIdle());
      }
      return true;
    },
  };
}

/*
    ____,-------------------------------,____
    \   |             Helpers           |   /
    /___|-------------------------------|___\
*/

function mapAllowedAction(
  side: Props['side'],
  turnColor: Color,
  allowPremove: boolean,
  gameOver: boolean,
): AllowedAction {
  if (gameOver || side === 'none') return 'none';
  if (side === 'all') return 'move';

  const turnSide = turnColor === 'w' ? 'white' : 'black';

  if (side === turnSide) return 'move';
  return allowPremove ? 'premove' : 'none';
}

function mapWinner(chess: Chess): 'draw' | 'white' | 'black' {
  if (chess.isDraw()) return 'draw';
  return chess.turn() === 'b' ? 'white' : 'black';
}

function mapMoveToPieceCode(move: Move): PieceCode {
  return `${move.color}${move.piece.toLocaleUpperCase()}` as PieceCode;
}

function isEqualMove(a?: MoveInfo, b?: MoveInfo) {
  return a === b || (a && b && a.from === b.from && a.to === b.to && a.promotion === b.promotion);
}

function isEqualSelection(a: MoveInfo | undefined, b: SelectionFSM) {
  return (
    (!a && b.name === 'idle') || (a && a.from === b.from && a.to === b.to && b.action === 'move')
  );
}

function mapInteractive(
  allow: AllowedAction,
  turnColor: Color,
  allowOutOfOrder?: boolean,
): Required<ChessBoardProps>['interactive'] {
  switch (allow) {
    case 'none':
      return 'none';
    case 'move':
      if (allowOutOfOrder) return 'all';
      return turnColor === 'w' ? 'white' : 'black';
    case 'premove':
      return turnColor === 'w' ? 'black' : 'white';
  }
}
